'use client';

import { useEffect, useRef, useState } from 'react';
import type WaveSurfer from 'wavesurfer.js';

import { IconPauseRound, IconPlayRoundFill } from '@/components/apps/ui/icon';

import { IndustrialCaseMediaCaption } from './IndustrialCaseMediaCaption';

type IndustrialCaseAudioPlayerProps = {
  /**
   * ### caption
   *
   * Author-written explanation shown below the player.
   */
  caption: string;
  /**
   * ### src
   *
   * Browser-playable audio URL emitted by the authoring upload flow.
   */
  src: string;
};

/**
 * ### IndustrialCaseAudioPlayer
 *
 * Renders an authored sound sample as an interactive WaveSurfer waveform.
 * Native audio controls remain available when decoding or visualization fails.
 *
 * @param props Uploaded audio metadata
 * @returns Accessible customer-case audio figure content
 */
export function IndustrialCaseAudioPlayer({
  caption,
  src,
}: IndustrialCaseAudioPlayerProps) {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<WaveSurfer | null>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const accessibleLabel = caption.trim() || '현장 소리';

  useEffect(() => {
    const container = waveformRef.current;
    if (!container) return undefined;

    let isMounted = true;
    let player: WaveSurfer | undefined;

    const initialize = async () => {
      try {
        const { default: WaveSurferClass } = await import('wavesurfer.js');
        if (!isMounted || !waveformRef.current) return;

        player = WaveSurferClass.create({
          barGap: 2,
          barRadius: 2,
          barWidth: 2,
          container: waveformRef.current,
          cursorColor: '#6d6d68',
          cursorWidth: 1,
          height: 64,
          normalize: true,
          progressColor: '#ff3d59',
          url: src,
          waveColor: '#c4c4bf',
        });
        playerRef.current = player;
        player.on('ready', (readyDuration) => {
          if (!isMounted) return;
          setDuration(readyDuration);
          setIsReady(true);
        });
        player.on('timeupdate', (time) => {
          if (isMounted) setCurrentTime(time);
        });
        player.on('play', () => {
          if (isMounted) setIsPlaying(true);
        });
        player.on('pause', () => {
          if (isMounted) setIsPlaying(false);
        });
        player.on('finish', () => {
          if (!isMounted) return;
          setCurrentTime(0);
          setIsPlaying(false);
        });
        player.on('error', () => {
          if (isMounted) setHasError(true);
        });
      } catch {
        if (isMounted) setHasError(true);
      }
    };

    void initialize();

    return () => {
      isMounted = false;
      player?.destroy();
      playerRef.current = null;
    };
  }, [src]);

  const togglePlayback = () => {
    if (!isReady || hasError) return;
    playerRef.current?.playPause();
  };

  return (
    <figure className="case-media case-media--audio">
      <div className="rounded-[2px] border border-[#d8d8d3] bg-[#f5f5f2] px-4 py-5 md:px-5">
        {hasError ? (
          <div>
            {/* Industrial inspection samples are non-verbal sounds. */}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio className="w-full" controls preload="metadata" src={src}>
              브라우저에서 이 오디오를 재생할 수 없습니다
            </audio>
            <p className="mt-3 text-sm font-medium leading-[1.5] text-[#686864]">
              파형을 만들지 못해 기본 재생기로 표시합니다
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-[48px_minmax(0,1fr)] items-center gap-4">
            <button
              type="button"
              className="grid size-12 touch-manipulation place-items-center rounded-full bg-[#171715] text-white transition-[background-color,transform] duration-150 ease-out hover:bg-[#ff3d59] active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-[#ff3d59] disabled:cursor-wait disabled:opacity-45 motion-reduce:transform-none motion-reduce:transition-none"
              aria-label={`${accessibleLabel} ${isPlaying ? '일시정지' : '재생'}`}
              aria-pressed={isPlaying}
              disabled={!isReady}
              onClick={togglePlayback}
            >
              {isPlaying ? (
                <IconPauseRound size={20} aria-hidden="true" />
              ) : (
                <IconPlayRoundFill size={20} aria-hidden="true" />
              )}
            </button>
            <div className="min-w-0">
              <div
                ref={waveformRef}
                className="min-h-16 min-w-0 touch-pan-y overflow-hidden"
                aria-hidden="true"
              />
              <div className="mt-2 flex items-center justify-between gap-4 text-xs font-semibold leading-none text-[#6f6f6a] tabular-nums">
                <span className="truncate">{accessibleLabel}</span>
                <span className="shrink-0">
                  {formatMediaTime(currentTime)} / {formatMediaTime(duration)}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      <IndustrialCaseMediaCaption caption={caption} />
    </figure>
  );
}

function formatMediaTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const wholeSeconds = Math.floor(seconds);
  const minutes = Math.floor(wholeSeconds / 60);
  const remainder = String(wholeSeconds % 60).padStart(2, '0');
  return `${minutes}:${remainder}`;
}
